import type { FormEvent } from 'react';
import type {
  ButtonVariant,
  DeliveryOption,
  PaymentOption,
  PromotionStatus,
  PurchaseAction,
} from './constants';

export interface PromoBadge {
  altText: string;
  src: string;
}

export interface Image {
  altText: string | null;
  captionsSrc?: string | null;
  mainSrc: string | null;
  thumbnailSrc: string | null;
  videoSrc?: string | null;
}

export interface Link {
  href: string | null;
  label: string | null;
}

export interface BreadCrumbLink extends Link {
  selected: boolean;
}

export const ProductType = {
  Book: 'BookProduct',
  Generic: 'GenericProduct',
  Gift: 'GiftCardProduct',
} as const;

export type ProductType = (typeof ProductType)[keyof typeof ProductType];

export interface OrderItem
  extends Omit<Product, 'format' | 'promotion' | 'purchaseAction'> {
  brand?: string | null;
  category?: string[] | null;
  easonsProductId?: string | null;
  quantity: number;
  variant?: string | null;
}

export interface Urls {
  account?: string;
  basket?: string;
  checkout?: string;
  login?: string;
  logout?: string;
  sagePayScript?: string;
}

export interface GiftCard {
  applied: string;
  cardNumber: string;
  isApplied: boolean;
  remaining: string;
}

export interface PaymentAmounts {
  discount: string;
  discountDecimal: number;
  couponDiscountDecimal?: number;
  giftCard: GiftCard[] | null;
  loyaltyCard: string;
  shipping: string;
  shippingDecimal: number;
  linesubtotal: string;
  subtotal: string;
  subtotalDecimal: number;
  tax: string;
  taxDecimal: number;
  total: string;
  totalDecimal: number;
  loyaltyCardDecimal: number;
  giftCardTotalDecimal: number;
}

export interface County {
  countyCode: string;
  countyName: string;
}

export interface Country {
  countryCode: string;
  countryName: string;
}

export interface Address extends County, Country {
  id?: string;
  firstName: string;
  lastName: string;
  company: string;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  phone: string;
  isPrimary?: boolean;
}

export interface AddressPayload
  extends Omit<Address, 'countryName' | 'countyName' | 'isPrimary'> {}

export type AddressFieldKey = keyof Omit<AddressPayload, 'id'>;

export interface CartServiceMessage {
  ctaLink: Link | null;
  header: string | null;
  messageBody: string | null;
}

export interface StoreLocations {
  id: string | null;
  company: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  county: string | null;
  postalCode: string | null;
}

export interface CheckoutData {
  amounts: PaymentAmounts | null;
  cartId: string | null;
  cartName: string | null;
  clickAndCollectLocations: StoreLocations[] | null;
  discountCode: string | null;
  emailAddress: string | null;
  hasBalance: boolean;
  isClickAndCollectAvailable: boolean;
  items: OrderItem[] | null;
  loyaltyCardId: string | null;
  promotions: Promotion[] | null;
  shippingMethod: string | null;
}

type BookFormat = 'Hardback' | 'Paperback';

interface ProductPrice {
  current: string;
  former: string | null;
  // GA4 numeric selling price, alongside the formatted `current` string.
  amount?: number;
}

export interface Product {
  author: string | null;
  easonsProductId?: string | null;
  variant?: string | null;
  format: BookFormat | null;
  image: Image | null;
  loyaltyPoints: number;
  maxQuantity: number;
  name: string | null;
  price: ProductPrice | null;
  productType: ProductType | null;
  productUrl: string | null;
  promoBadge: PromoBadge | null;
  promotion?: string;
  purchaseAction: PurchaseActionType;
  sku: string | null;
  description?: string | null;
  staffReview?: string | null;
  staffReviewTitle?: string | null;
  rank?: number | null;
  // GA4 primary-first category list for view_item_list / select_item.
  category?: string[] | null;
}

export interface RecommendedProducts {
  heading: string;
  description?: string;
  key: string;
  /** GA4 item_list_id - Optimizely ContentLink.ID. */
  contentId?: number;
  viewAllLink?: string;
  viewAllText?: string;
  showAddToCart?: boolean;
  showDispatchMessage?: boolean;
  showMoreLikeThis?: boolean;
  autoplay?: boolean;
  desktopSlides?: number;
  surface?: string;
  products?: Product[];
  productsEndpoint?: string;
  productCode?: string;
}

interface ProductMain extends Product {
  carouselImages?: Image[];
  jellybooksUrl?: string | null;
  otherFormats?: Product[];
}

export interface StoreReview {
  date: string;
  heading: string;
  rating: number;
  review: string;
  reviewHeading: string;
  reviewId: string;
}

export interface SearchBannerData {
  image: {
    urls: Array<{ url: string }>;
    title?: string;
  };
  link?: {
    url: string;
    target?: '_blank' | '_self';
  };
}

export interface ReviewsCarouselData {
  key: string;
  reviews: StoreReview[];
}

interface HelpText {
  applyLoyaltyBalance?: string;
  giftCardPinCode: string;
  loyaltyCard?: string;
  loginHelpText?: string;
  signupLabel?: string;
}

interface Promotion {
  status: PromotionStatusType;
  text: string | null;
}

export interface PageData {
  breadcrumbLinks?: BreadCrumbLink[];
  cartServiceMessage: CartServiceMessage | null;
  countries?: Country[];
  excludedCountriesForBilling?: string[];
  additionalCountriesForBilling?: Country[];
  footerLinks?: Link[];
  helpText?: HelpText;
  product?: ProductMain;
  recommendations?: RecommendedProducts[];
  subheading?: string;
  consentMessage?: string;
  termsConditions?: string;
  urls: Urls;
  isQuickPayEnabled: boolean;
  isLoyaltyEnabled?: boolean;
  isGiftCardsEnabled?: boolean;
  isPayPalEnabled?: boolean;
}

export interface UserData {
  deliveryAddresses: Address[];
  email?: string;
  isAuthenticated: boolean;
  loyaltyCardId?: string;
}

type ValidationError = string | string[];

export type ValidationErrors = Record<string, ValidationError>;

export type ValueOf<T> = T[keyof T];

export type ButtonVariantType = ValueOf<typeof ButtonVariant>;

export type DeliveryOptionType = ValueOf<typeof DeliveryOption> | '';

export type PaymentOptionType = ValueOf<typeof PaymentOption> | '';

export type PromotionStatusType = ValueOf<typeof PromotionStatus>;

export type PurchaseActionType = ValueOf<typeof PurchaseAction>;

export type Handler = (e: FormEvent<HTMLFormElement>) => void;

export type URLSearchParamsQuery =
  | string
  | string[][]
  | Record<string, string>
  | URLSearchParams
  | undefined;

export interface Ga4Promotion {
  promotion_id: string;
  promotion_name: string;
  creative_name?: string;
  creative_slot?: string;
  index?: number;
}

/**
 * Identifies a product list for GA4. `name` is the human-readable label
 * (`item_list_name`); `id` is the Optimizely ContentLink.ID as a string
 * (`item_list_id`), omitted when the surface has no content id.
 */
export interface ListIdentity {
  name: string;
  id?: string;
}

export interface Ga4Item {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  index?: number;
  item_list_name?: string;
  // Optimizely ContentLink.ID as a string. Omitted when the surface has none -
  // a literal "0" would collide across unrelated lists in GA4 reporting.
  item_list_id?: string;
  // Not a GA4 reserved param — GA4 items have no image field. Carried so the
  // GTM builders for Klaviyo (`UCV - Klaviyo - item`) and Meta can read a
  // product image off the same `ecommerce.items` array.
  image_url?: string;
}

// Legacy UA Enhanced Ecommerce product. The GTM container's Data Layer
// Variables read these key names off `ecommerce.add.products` /
// `ecommerce.remove.products` — do not rename. Emitted alongside `Ga4Item`
// during the UA→GA4 transition.
export interface DataLayerProperties {
  id: string;
  name: string;
  brand: string;
  category: string[];
  variant: string;
  price: number;
  quantity: number;
  image_url?: string;
}

// `addShippingInfo` only. The legacy product shape with the `category` array
// replaced by GA4-style level keys, so the container reads a category level as
// a plain string rather than indexing into an array. Client requirement: this
// one event carries the levels and no `category`; every other legacy event
// keeps `DataLayerProperties` unchanged.
export interface ShippingInfoProduct
  extends Omit<DataLayerProperties, 'category'> {
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
}

export interface NavSubmenuLinkProps {
  href: string;
  target?: string;
  textContent: string;
}

export interface NavSubsection {
  heading: NavSubmenuLinkProps;
  isFeatured: boolean;
  links: NavSubmenuLinkProps[];
}

// #2947 The colourways a highlighted menu item may use. Mirrors the HighlightMenuColour
// selection factory; the values are the CSS modifier suffixes the Razor partial emits.
export type HighlightColour = 'christmas-red' | 'black-friday';

export interface NavLinkProps extends NavSubmenuLinkProps {
  // #2947 Set only when the landing page's "Highlight Menu" tick is on, so its presence is
  // what marks the item as highlighted. Only top-level trunks can be, hence not on
  // NavSubmenuLinkProps.
  highlightColour?: HighlightColour;
  submenu?: NavSubsection[];
}

export interface StandardApiResponse {
  data?: unknown;
  isSuccess: boolean;
  messages?: string[];
}

export type OrderPayload = {
  billingAddress: Address;
  clickAndCollectStoreId: string;
  deliveryOption: DeliveryOptionType;
  email?: string;
  emailOptInNewsAndOffers: boolean;
  giftMessage: string;
  isGift: boolean;
  shippingAddress: Address;
};

export interface CategoryFacet {
  key: string;
  name: string;
  selected: boolean;
  count: number;
  url?: string | null;
  children?: CategoryFacet[] | null;
}

export interface CategoryFacetGroup {
  groupName: string;
  groupLabel: string;
  groupFieldName: string;
  facetType: 0 | 1 | 2;
  showFacetCount: boolean;
  facets: CategoryFacet[];
  parentPage?: { name: string; url: string } | null;
}

export interface CategorySortOption {
  value: string;
  text: string;
  selected: boolean;
}

export interface CategoryPage {
  products: Product[];
  facetGroups: CategoryFacetGroup[];
  sorting: CategorySortOption[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  inStock: boolean;
  algoliaQueryId?: string | null;
  nextPageUrl?: string | null;
  prevPageUrl?: string | null;
}

export interface CategoryData extends CategoryPage {
  filterEndpoint: string;
  categoryBaseUrl: string;
  query: string;
  /** GA4 item_list_name - the category page name, not the search term. */
  listName: string;
  /** GA4 item_list_id - Optimizely ContentLink.ID. */
  contentId?: number;
  noResultsLabel: string;
  noResultsImage: string | null;
  showMoreLikeThis?: boolean;
  algoliaFilters?: string[];
}

export interface CategoryFilterState {
  sort: string;
  inStock: boolean;
  selectedFacets: Record<string, string[]>;
}

export interface HeroBannerSlide {
  desktopImage: string;
  mobileImage?: string;
  desktopImageAltText: string;
  mobileImageAltText?: string;
  videoUrl?: string;
  backgroundTheme: string;
  eyebrow?: string;
  title?: string;
  titleEmphasis?: string;
  subtitle?: string;
  ctaText?: string;
  link?: string;
}

export interface HeroBannerData {
  slides: HeroBannerSlide[];
  autoplay: boolean;
  autoplayInterval: number;
}
